import { LinearGradient } from "expo-linear-gradient";
import React, { useEffect, useRef, useState } from "react";
import { Animated, AppState, Easing, Platform, StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import { TeachingLiveActivityStream, type LiveActivityContent } from "./TeachingLiveActivityStream";
import { useLoopValue, usePulseValue } from "./useAnimationHelpers";

import { colors, fonts, radius } from "@/lib/theme";

const USE_NATIVE = Platform.OS !== "web";

interface Props {
    // Wall-clock epoch (ms) when generation started. Phase is derived from
    // elapsed time (same contract as PocGeneratingAnimation) so a
    // background→foreground transition resolves to the correct phase instantly.
    startedAt: number;
    // "pages" = the teaching motif (sheets writing themselves). "halo" = the
    // original POC rings+core motif, kept for parity with Plan of Care.
    variant?: "pages" | "halo";
    // The visit's real sections / answer count for the live-activity panel.
    // Forwarded rather than read here, because this component has no visit
    // context of its own.
    content?: LiveActivityContent;
}

function pillLabelForPhase(elapsedSec: number): string {
    if (elapsedSec < 8) {
        return "Phase 1 · Reading Assessment";
    }
    if (elapsedSec < 22) {
        return "Phase 2 · Choosing Topics";
    }
    if (elapsedSec < 45) {
        return "Phase 3 · Writing Teaching";
    }
    return "Phase 4 · Final Checks";
}

export function TeachingGeneratingAnimation({ startedAt, variant = "pages", content }: Props) {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const tick = () => setNow(Date.now());
        const interval = setInterval(tick, 1000);
        const sub = AppState.addEventListener("change", (state) => {
            if (state === "active") {
                tick();
            }
        });
        return () => {
            clearInterval(interval);
            sub.remove();
        };
    }, []);

    const elapsedSec = Math.max(0, (now - startedAt) / 1000);
    const pillDotOpacity = usePulseValue(1200, 0.4, 1);

    return (
        <View style={styles.container}>
            <View style={styles.hero}>
                {variant === "pages" ? <PagesHero /> : <HaloHero />}

                <LinearGradient
                    colors={[colors.teal50, colors.pink50]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={styles.pill}
                >
                    <Animated.View style={[styles.pillDot, { opacity: pillDotOpacity }]} />
                    <Text style={styles.pillText}>{pillLabelForPhase(elapsedSec)}</Text>
                </LinearGradient>

                <Text style={styles.title}>Generating Teaching</Text>
                <Text style={styles.subtitle}>
                    Matching education to the assessment findings, then writing it for the patient
                    and caregiver.
                </Text>
                <Text style={styles.aside}>
                    Up to <Text style={styles.asideStrong}>60 seconds</Text> — writing this by hand
                    takes about 15 minutes.
                </Text>
            </View>

            <TeachingLiveActivityStream {...content} />
        </View>
    );
}

// ── Hero A: "Pages" — three sheets, the front one writing itself line by line ──

const LINE_GRADIENTS: [string, string][] = [
    [colors.teal400, "#5EC4D0"],
    ["#5EC4D0", "#B06EE8"],
    ["#B06EE8", colors.pink400],
    [colors.pink400, colors.pink300]
];

function PagesHero() {
    const glow = usePulseValue(3200, 0.45, 0.9);
    const float = useLoopValue(3600);
    const spark = usePulseValue(2200, 0.25, 1);

    const lift = float.interpolate({ inputRange: [0, 0.5, 1], outputRange: [0, -5, 0] });
    const liftBack = float.interpolate({ inputRange: [0, 0.5, 1], outputRange: [0, -9, 0] });

    return (
        <View style={styles.pagesWrapper}>
            <Animated.View style={[styles.pagesGlow, { opacity: glow }]}>
                <LinearGradient
                    colors={[
                        "rgba(69,210,176,0.35)",
                        "rgba(231,72,245,0.22)",
                        "rgba(255,255,255,0)"
                    ]}
                    start={{ x: 0.2, y: 0.1 }}
                    end={{ x: 1, y: 1 }}
                    style={StyleSheet.absoluteFill}
                />
            </Animated.View>

            <Animated.View
                style={[
                    styles.sheetBack,
                    { left: 16, transform: [{ translateY: liftBack }, { rotate: "-8deg" }] }
                ]}
            />
            <Animated.View
                style={[
                    styles.sheetBack,
                    { right: 16, transform: [{ translateY: liftBack }, { rotate: "6deg" }] }
                ]}
            />

            <Animated.View style={[styles.sheetFront, { transform: [{ translateY: lift }] }]}>
                {LINE_GRADIENTS.map((pair, i) => (
                    <WriteLine key={i} colors={pair} delay={i * 500} last={i === 3} />
                ))}
                <Animated.View style={[styles.spark, { opacity: spark }]}>
                    <LinearGradient
                        colors={[colors.teal400, colors.pink400]}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={StyleSheet.absoluteFill}
                    />
                </Animated.View>
            </Animated.View>
        </View>
    );
}

interface WriteLineProps {
    colors: [string, string];
    delay: number;
    last?: boolean;
}

// One skeleton line that "writes" left→right, holds, then restarts. Width is a
// layout prop, so this animation cannot use the native driver.
function WriteLine({ colors: pair, delay, last }: WriteLineProps) {
    const progress = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        const loop = Animated.loop(
            Animated.sequence([
                Animated.delay(delay),
                Animated.timing(progress, {
                    toValue: 1,
                    duration: 2000,
                    easing: Easing.out(Easing.cubic),
                    useNativeDriver: false
                }),
                Animated.delay(1400),
                Animated.timing(progress, {
                    toValue: 0,
                    duration: 0,
                    useNativeDriver: false
                })
            ])
        );
        loop.start();
        return () => loop.stop();
    }, [progress, delay]);

    const width = progress.interpolate({ inputRange: [0, 1], outputRange: ["0%", "100%"] });

    return (
        <View style={[styles.lineTrack, last && styles.lineTrackShort]}>
            <Animated.View style={[styles.lineFillWrap, { width }]}>
                <LinearGradient
                    colors={pair}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={StyleSheet.absoluteFill}
                />
            </Animated.View>
        </View>
    );
}

// ── Hero B: "Halo" — the original POC rings + pulsing gradient core ──

function HaloHero() {
    const outerSpin = useLoopValue(1900);
    const innerSpin = useLoopValue(1300);
    const orbit = useLoopValue(6000);
    const coreScale = usePulseValue(1400, 1, 1.55);
    const coreOpacity = usePulseValue(1400, 0.45, 1);

    const outerRotate = outerSpin.interpolate({
        inputRange: [0, 1],
        outputRange: ["0deg", "360deg"]
    });
    const innerRotate = innerSpin.interpolate({
        inputRange: [0, 1],
        outputRange: ["360deg", "0deg"]
    });
    const orbitRotate = orbit.interpolate({ inputRange: [0, 1], outputRange: ["0deg", "360deg"] });

    return (
        <View style={styles.haloWrapper}>
            <Animated.View style={[styles.outerRing, { transform: [{ rotate: outerRotate }] }]} />
            <Animated.View style={[styles.innerRing, { transform: [{ rotate: innerRotate }] }]} />
            <Animated.View style={[styles.orbit, { transform: [{ rotate: orbitRotate }] }]}>
                <View style={[styles.satellite, styles.satelliteTop]} />
                <View style={[styles.satelliteSm, styles.satelliteLeft]} />
                <View style={[styles.satelliteSm, styles.satelliteRight]} />
            </Animated.View>
            <Animated.View
                style={[styles.core, { opacity: coreOpacity, transform: [{ scale: coreScale }] }]}
            >
                <LinearGradient
                    colors={[colors.teal400, colors.pink400]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={StyleSheet.absoluteFill}
                />
            </Animated.View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1
    },
    hero: {
        alignItems: "center",
        paddingHorizontal: 20,
        paddingTop: 26,
        paddingBottom: 12
    },

    // Pages hero
    pagesWrapper: {
        width: 140,
        height: 124,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 18
    },
    pagesGlow: {
        position: "absolute",
        width: 140,
        height: 140,
        borderRadius: 70,
        overflow: "hidden"
    },
    sheetBack: {
        position: "absolute",
        width: 76,
        height: 92,
        borderRadius: 12,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.gray200,
        shadowColor: "#0F0A28",
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.08,
        shadowRadius: 14,
        elevation: 2
    },
    sheetFront: {
        width: 86,
        height: 100,
        borderRadius: 14,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.gray200,
        paddingHorizontal: 12,
        paddingVertical: 14,
        gap: 9,
        shadowColor: "#0F0A28",
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.12,
        shadowRadius: 24,
        elevation: 5
    },
    lineTrack: {
        height: 6,
        borderRadius: 3,
        backgroundColor: colors.gray100,
        overflow: "hidden"
    },
    lineTrackShort: {
        width: "66%"
    },
    lineFillWrap: {
        height: "100%",
        borderRadius: 3,
        overflow: "hidden"
    },
    spark: {
        position: "absolute",
        bottom: -6,
        right: -6,
        width: 14,
        height: 14,
        borderRadius: 4,
        overflow: "hidden"
    },

    // Halo hero
    haloWrapper: {
        width: 132,
        height: 124,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 18
    },
    outerRing: {
        position: "absolute",
        width: 104,
        height: 104,
        borderRadius: 52,
        borderWidth: 3,
        borderColor: colors.border,
        borderTopColor: colors.teal400,
        borderRightColor: colors.pink400
    },
    innerRing: {
        position: "absolute",
        width: 74,
        height: 74,
        borderRadius: 37,
        borderWidth: 2,
        borderColor: "transparent",
        borderBottomColor: colors.pink400,
        borderLeftColor: colors.teal400
    },
    orbit: {
        position: "absolute",
        width: 132,
        height: 132
    },
    satellite: {
        position: "absolute",
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: colors.teal400
    },
    satelliteSm: {
        position: "absolute",
        width: 6,
        height: 6,
        borderRadius: 3
    },
    satelliteTop: {
        top: 0,
        left: 62
    },
    satelliteLeft: {
        bottom: 6,
        left: 14,
        backgroundColor: colors.pink200
    },
    satelliteRight: {
        bottom: 6,
        right: 14,
        backgroundColor: colors.teal200
    },
    core: {
        width: 12,
        height: 12,
        borderRadius: 6,
        overflow: "hidden"
    },

    // Copy
    pill: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingHorizontal: 12,
        paddingVertical: 5,
        borderRadius: radius.full,
        marginBottom: 10
    },
    pillDot: {
        width: 5,
        height: 5,
        borderRadius: 2.5,
        backgroundColor: colors.teal400
    },
    pillText: {
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors.teal700,
        letterSpacing: 1.2,
        textTransform: "uppercase"
    },
    title: {
        fontSize: 22,
        fontFamily: fonts.extraBold,
        color: colors.textPrimary,
        letterSpacing: -0.4,
        marginBottom: 6,
        textAlign: "center"
    },
    subtitle: {
        fontSize: 13,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        lineHeight: 19,
        textAlign: "center",
        maxWidth: 300,
        marginBottom: 6
    },
    aside: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.textTertiary,
        lineHeight: 18,
        textAlign: "center",
        fontStyle: "italic",
        maxWidth: 300,
        paddingHorizontal: 12
    },
    asideStrong: {
        fontFamily: fonts.bold,
        color: colors.teal600,
        fontStyle: "normal"
    }
});
